import { Injectable, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';

import { RepricingConfirmationDetails } from '../models/manual-repricing.model';

export interface UnsafeRepricingConfirmModalState {
  visible: boolean;
  details: RepricingConfirmationDetails | null;
  detailsList: RepricingConfirmationDetails[];
  selectedProductIds: string[];
}

const INITIAL_STATE: UnsafeRepricingConfirmModalState = {
  visible: false,
  details: null,
  detailsList: [],
  selectedProductIds: [],
};

@Injectable({ providedIn: 'root' })
export class UnsafeRepricingConfirmModalService {
  readonly state = signal<UnsafeRepricingConfirmModalState>(INITIAL_STATE);

  private decision$?: Subject<string[]>;

  requestConfirmation(details: RepricingConfirmationDetails): Observable<string[]> {
    return this.requestConfirmations([details]);
  }

  requestConfirmations(
    detailsList: RepricingConfirmationDetails[]
  ): Observable<string[]> {
    this.decision$?.complete();
    this.decision$ = new Subject<string[]>();
    this.state.set({
      visible: true,
      details: detailsList[0] ?? null,
      detailsList,
      selectedProductIds:
        detailsList.length === 1 ? [detailsList[0].product_id] : [],
    });
    return this.decision$.asObservable().pipe(take(1));
  }

  setSelection(productId: string, selected: boolean): void {
    this.state.update((state) => {
      const selectedIds = new Set(state.selectedProductIds);
      if (selected) {
        selectedIds.add(productId);
      } else {
        selectedIds.delete(productId);
      }

      return {
        ...state,
        selectedProductIds: Array.from(selectedIds),
      };
    });
  }

  confirm(): void {
    this.resolve(this.state().selectedProductIds);
  }

  cancel(): void {
    this.resolve([]);
  }

  close(): void {
    this.cancel();
  }

  private resolve(selectedProductIds: string[]): void {
    const decision = this.decision$;
    this.decision$ = undefined;
    this.state.set(INITIAL_STATE);
    decision?.next(selectedProductIds);
    decision?.complete();
  }
}
